import { useCallback, useEffect, useState, useRef } from "react";
import { useRecoilState } from "recoil";

import { useUser } from "../useUser";
import { useStatsigClient } from "@statsig/react-bindings";
import { ActiveAdAtom } from "MVVM/State/ads";
import { IAllstarUser } from "MVVM/Views/Setup/Components/@types";
import { analytics } from "MVVM/Analytics";
const sendPauseVideo = () => {
  window.parent.postMessage(
    {
      action: "pauseVideo",
    },
    "*",
  );
};

const sendResumeVideo = () => {
  window.parent.postMessage(
    {
      action: "resumeVideo",
    },
    "*",
  );
};

interface UsePlaywireAdsParams {
  onComplete?: () => void;
  onAdStarted?: () => void;
  path?: string;
  onShowHiddenContainer?: () => void;
}

const checkExperiment = ({ allstarUser }: { allstarUser: IAllstarUser }) => {
  const testingAds =
    new URLSearchParams(window.location.search).get("pw_test_ads") === "true";
  if (testingAds) {
    return true;
  }
  const adApplicableUser =
    ["FREE", "INTERNAL", "ESSENTIALS"].includes(
      allstarUser?.user?.userGroup?.type,
    ) || !allstarUser?.loggedIn;

  return adApplicableUser;
};

export const usePlaywireAds = ({
  onAdStarted,
  onComplete,
  path = "default",
  onShowHiddenContainer,
}: UsePlaywireAdsParams = {}) => {
  const { allstarUser } = useUser();
  const { getExperiment } = useStatsigClient();
  const [activeAd, setActiveAd] = useRecoilState(ActiveAdAtom);
  const listenersSetupRef = useRef(false);
  const callbacksRef = useRef({
    onAdStarted,
    onComplete,
    onShowHiddenContainer,
    path,
  });

  useEffect(() => {
    callbacksRef.current = {
      onAdStarted,
      onComplete,
      onShowHiddenContainer,
      path,
    };
  }, [onAdStarted, onComplete, onShowHiddenContainer, path]);

  useEffect(() => {
    return () => {
      setActiveAd("");
    };
  }, [setActiveAd]);

  const triggerAd = useCallback(() => {
    const canShowAds = checkExperiment({ allstarUser });

    if (!window.ramp?.spaAds || !!activeAd || !canShowAds) {
      return onShowHiddenContainer?.();
    }

    const setupEventListeners = () => {
      if (window.Bolt && !listenersSetupRef.current) {
        listenersSetupRef.current = true;

        window.Bolt.on(
          "precontent_ad_video",
          window.Bolt.BOLT_AD_STARTED,
          () => {
            const { path, onAdStarted } = callbacksRef.current;
            analytics.track("Ad Started", {
              adType: "precontent_ad_video",
              adPath: path,
            });
            sendPauseVideo();
            setActiveAd(path);
            onAdStarted?.();
          },
        );
        window.Bolt.on(
          "precontent_ad_video",
          window.Bolt.BOLT_AD_COMPLETE,
          () => {
            const { onComplete } = callbacksRef.current;
            onComplete?.();
            setActiveAd("");
            sendResumeVideo();
          },
        );
        window.Bolt.on("precontent_ad_video", "showHiddenContainer", () => {
          const { onShowHiddenContainer } = callbacksRef.current;
          setActiveAd("");
          onShowHiddenContainer?.();
          sendResumeVideo();
        });
        window.Bolt.on("precontent_ad_video", window.Bolt.BOLT_AD_ERROR, () => {
          const { onShowHiddenContainer } = callbacksRef.current;
          setActiveAd("");
          onShowHiddenContainer?.();
          sendResumeVideo();
        });
      }
    };

    if (window.ramp) {
      if (window.Bolt) {
        setupEventListeners();
      } else {
        window.ramp.onPlayerReady = setupEventListeners;
      }
    }

    analytics.track("Ad Requested", {
      adType: "precontent_ad_video",
      adPath: path,
    });
    window.ramp!.spaAds({
      ads: {
        type: "precontent_ad_video",
      },
      countPageview: false,
      path: path,
    });
  }, [activeAd, allstarUser, getExperiment]);

  return { triggerAd, activeAd };
};
